import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { userAnnotationState } from "@/state/UserAnnotations";
import { annotationState } from "@/state/Annotations";
import { DodonaElement } from "@/components/meta/dodona_element";

/**
 * A separate layer that contains the marking for the selection.
 * It is separate from code-layers to make the rendering independent and thus much faster.
 *
 * The selected code is styled to look like a user annotation.
 * It is used to mark the selected code while editing an annotation
 * @element d-selection-layer
 *
 * @prop {number} row - The row number
 * @prop {string} code - The raw source code of the line.
 */
@customElement("d-selection-layer")
class SelectionLayer extends DodonaElement {
    @property({ type: Number })
    row: number;

    @property({ type: String })
    code = "";

    get shouldMarkSelection(): boolean {
        return (
            userAnnotationState.selectedRange &&
            userAnnotationState.selectedRange.row <= this.row &&
            userAnnotationState.selectedRange.row + (userAnnotationState.selectedRange.rows ?? 1) > this.row
        );
    }

    get markingClass(): string {
        return annotationState.isQuestionMode ? "question-selection-marker" : "annotation-selection-marker";
    }

    render(): TemplateResult {
        if (!this.shouldMarkSelection) {
            return html``;
        }

        if (!userAnnotationState.selectedRange.column && !userAnnotationState.selectedRange.columns) {
            return html`<pre class="code-line selection-layer ${this.markingClass}" aria-hidden="true">
${this.code}</pre>`;
        }

        const start = userAnnotationState.selectedRange.column ?? 0;
        const end = userAnnotationState.selectedRange.columns
            ? start + userAnnotationState.selectedRange.columns
            : this.code.length;

        const selectionLayer: (TemplateResult | string)[] = [
            html`<span aria-hidden="true"></span>`, // height hack; hide it from SRs
            this.code.substring(0, start),
            html`<span class="${this.markingClass}">${this.code.substring(start, end)}</span>`,
            this.code.substring(end),
        ];

        return html`<pre class="code-line selection-layer" aria-hidden="true">${selectionLayer}</pre>`;
    }
}
